import React from "react";

import { getAyurmaStrings } from "../i18n/ayurma";
import { ReportLang } from "../i18n/reportLang";
import CaloricNutritionPage from "../pages/whiteLabel/CaloricNutritionPage/CaloricNutritionPage";
import CoverResultsPage from "../pages/whiteLabel/CoverResultsPage/CoverResultsPage";
import MovementPlanPage from "../pages/whiteLabel/MovementPlanPage/MovementPlanPage";
import NutritionGuidePage from "../pages/whiteLabel/NutritionGuidePage/NutritionGuidePage";
import SourcesDisclaimerPage from "../pages/whiteLabel/SourcesDisclaimerPage/SourcesDisclaimerPage";
import { WhiteLabelPageProps } from "../pages/whiteLabel/types";
import {
  DEFAULT_WHITE_LABEL_PAGE_ORDER,
  WhiteLabelPageKey,
  WhiteLabelTheme,
} from "../whiteLabel/whiteLabelTheme";
import { SvgConverterType } from "./Report";

type Props = {
  data: any;
  theme: WhiteLabelTheme;
  lang: ReportLang;
  svgConverter: SvgConverterType;
  reportType: string;
  isCreatingPDF: boolean;
};

/**
 * Report shell for white-label RMR variants — a condensed, fully branded
 * document that shares no pages with the default RMR flow.
 *
 * Reached from ReportRMR via the theme registry, so no new branch was added to
 * Report.tsx's router and ReportAMR is untouched.
 *
 * Pagination contract: every entry below must render exactly one Layout-sized
 * page, except the last one. Only the final page in the order may spill onto an
 * extra page (the sources list is data-dependent), because a variable page count
 * mid-document would desynchronize the footer numbering that follows it.
 */
const WhiteLabelReportRMR = ({
  data,
  theme,
  lang,
  svgConverter,
  isCreatingPDF,
}: Props): JSX.Element => {
  const t = getAyurmaStrings(lang);

  const shared: Omit<WhiteLabelPageProps, "pageNumber"> = {
    data,
    theme,
    lang,
    t,
  };

  const pageMap: Record<
    WhiteLabelPageKey,
    (pageNumber: number) => JSX.Element
  > = {
    coverResults: (pageNumber) => (
      <CoverResultsPage
        {...shared}
        pageNumber={pageNumber}
        svgConverter={svgConverter}
      />
    ),
    caloricNutrition: (pageNumber) => (
      <CaloricNutritionPage
        {...shared}
        pageNumber={pageNumber}
        svgConverter={svgConverter}
        isCreatingPDF={isCreatingPDF}
      />
    ),
    nutritionGuide: (pageNumber) => (
      <NutritionGuidePage {...shared} pageNumber={pageNumber} />
    ),
    movementPlan: (pageNumber) => (
      <MovementPlanPage {...shared} pageNumber={pageNumber} />
    ),
    sourcesDisclaimer: (pageNumber) => (
      <SourcesDisclaimerPage {...shared} pageNumber={pageNumber} />
    ),
  };

  const pageOrder = theme.pageOrder ?? DEFAULT_WHITE_LABEL_PAGE_ORDER;

  return (
    <>
      {pageOrder.map((key, index) => {
        const renderPage = pageMap[key];
        return renderPage ? (
          <React.Fragment key={key}>{renderPage(index + 1)}</React.Fragment>
        ) : null;
      })}
    </>
  );
};

export default WhiteLabelReportRMR;
